import { CreditsPerMonth } from 'constants/credits';
import { useState, useMemo } from 'react';
import { BundleCatalog } from '@landr/pricing/lib/src/types';
import {
    Interval,
    PlanCode,
    SaleDto,
    SampleCatalog,
} from '@landr/pricing/lib/src/types/generated';
import { getReturnUrl } from 'helpers';
import { GetDiscountedPlan } from 'hooks/useGetDiscountedPlan/useGetDiscountedPlan';
import getYearlySaving from '../helpers/getYearlySaving';

const useUpsellActionsProvider = ({
    offeredPlanCodes,
    creditsNeeded = 0,
    samplesCatalog,
    studioCatalog,
    catalogSale,
    getDiscountedPlan,
}: {
    offeredPlanCodes: PlanCode[];
    creditsNeeded?: number;
    samplesCatalog?: SampleCatalog;
    studioCatalog?: BundleCatalog;
    catalogSale?: SaleDto;
    getDiscountedPlan: GetDiscountedPlan;
}) => {
    const showFrequencySwitch = creditsNeeded <= CreditsPerMonth;
    const [selectedPlan, setSelectedPlan] = useState<string>('');
    const [showYearly, setShowYearly] = useState(true);

    const frequency = showYearly
        ? ('Yearly' as Interval)
        : ('Monthly' as Interval);

    const handleFrequencyChange = (isYearly: boolean) => {
        setShowYearly(isYearly);
    };

    const handlePlanSelect = (planCode: string) => {
        setSelectedPlan(planCode);
    };

    const endDate = catalogSale?.endDate;
    const saleTitle = catalogSale?.prismicBanner?.title;
    const saleDesc = catalogSale?.prismicBanner?.description;

    const hasSale = offeredPlanCodes.some(
        (planCode) => !!getDiscountedPlan({ planCode, frequency: 'Yearly' }),
    );

    // Studio yearly offer is more advantageous, we only calculate the yearly saving on that plan
    const studioYearlySaving = getYearlySaving(studioCatalog?.plans);
    const samplesYearlySaving = getYearlySaving(samplesCatalog?.plans);

    const yearlySaving = Math.max(studioYearlySaving, samplesYearlySaving);

    const purchaseLink = useMemo(() => {
        if (!selectedPlan || !frequency) {
            return '';
        }

        const discountedPlan = getDiscountedPlan({
            planCode: selectedPlan,
            frequency,
        });
        const validCouponCode = discountedPlan ? discountedPlan.couponCode : '';

        return `/payment/subscription/checkout/${selectedPlan}/${frequency}/${validCouponCode}?returnUrl=${getReturnUrl()}`;
    }, [frequency, selectedPlan, getDiscountedPlan]);

    return {
        endDate,
        frequency,
        hasSale,
        saleTitle,
        saleDesc,
        purchaseLink,
        samplesCatalog,
        selectedPlan,
        showFrequencySwitch,
        showYearly,
        studioCatalog,
        yearlySaving,
        getDiscountedPlan,
        handleFrequencyChange,
        handlePlanSelect,
    };
};

export default useUpsellActionsProvider;
